import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FolderManager = ({ folders, onCreateFolder, onRenameFolder, onDeleteFolder }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderName, setRenameFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [error, setError] = useState('');

  const handleCreateFolder = () => {
    if (!newFolderName?.trim()) {
      setError('Folder name is required');
      return;
    }
    
    const exists = folders?.some(f => f?.name?.toLowerCase() === newFolderName?.trim()?.toLowerCase());
    if (exists) {
      setError('A folder with this name already exists');
      return;
    }

    onCreateFolder(newFolderName?.trim());
    setNewFolderName('');
    setError('');
    setShowCreateModal(false);
  };

  const handleRenameFolder = () => {
    if (!renameFolderName?.trim()) {
      setError('Folder name is required');
      return;
    }

    const exists = folders?.some(f => 
      f?.id !== selectedFolder?.id && 
      f?.name?.toLowerCase() === renameFolderName?.trim()?.toLowerCase()
    );
    if (exists) {
      setError('A folder with this name already exists');
      return;
    }

    onRenameFolder(selectedFolder?.id, renameFolderName?.trim());
    setRenameFolderName('');
    setError('');
    setShowRenameModal(false);
    setSelectedFolder(null);
  };

  const handleDeleteFolder = () => {
    onDeleteFolder(selectedFolder?.id);
    setShowDeleteModal(false);
    setSelectedFolder(null);
  };

  const openRenameModal = (folder) => {
    setSelectedFolder(folder);
    setRenameFolderName(folder?.name);
    setError('');
    setShowRenameModal(true);
  };

  const openDeleteModal = (folder) => {
    setSelectedFolder(folder);
    setShowDeleteModal(true);
  };

  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-md mx-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">{title}</h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        iconName="FolderPlus"
        iconPosition="left"
        onClick={() => {
          setError('');
          setNewFolderName('');
          setShowCreateModal(true);
        }}
      >
        New Folder
      </Button>

      {/* Create Folder Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Folder"
      >
        <div className="space-y-4">
          <Input
            label="Folder Name"
            placeholder="Enter folder name"
            value={newFolderName}
            onChange={(e) => {
              setNewFolderName(e?.target?.value);
              setError('');
            }}
            error={error}
            autoFocus
          />
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreateFolder}
            >
              Create Folder
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rename Folder Modal */}
      <Modal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Rename Folder"
      >
        <div className="space-y-4">
          <Input
            label="Folder Name"
            placeholder="Enter new folder name"
            value={renameFolderName}
            onChange={(e) => {
              setRenameFolderName(e?.target?.value);
              setError('');
            }}
            error={error}
            autoFocus
          />
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowRenameModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleRenameFolder}
            >
              Rename
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Folder Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Folder"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete the folder <span className="font-semibold text-foreground">&quot;{selectedFolder?.name}&quot;</span>? 
            Forms in this folder will be moved to &quot;All Forms&quot;.
          </p>
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFolder}
            >
              Delete Folder
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FolderManager;